import { useEffect, useRef, useState } from "preact/hooks"
import { useFormContext } from "../../context/form-context"
import { debounce } from "../../util/debounce"
import { Icon } from "./icon"

export const CitySelectOptions = (props) => {
  const logo = document.querySelector(".logo")
  const { toggleValue, ep, inFormValues } = useFormContext()
  const [showCityForm, setShowCityForm] = useState(false)
  const [initialName, setInitialName] = useState(props.title)
  const [initialValue, setInitialValue] = useState("")
  const [isLausanne, setIsLausanne] = useState(false)

  const onCityChange = (city) => {
    setLogo(
      city === "custom"
        ? undefined
        : props.cities.find((c) => c.value === city).name,
    )
    window.dispatchEvent(
      new CustomEvent("meldetool_city-change", { detail: city }),
    )
  }

  const setLogo = (_title) => {
    logo.setAttribute("city", gettext("Wir schauen hin"))
  }

  const handleCityChange = (city) => {
    const isLausanneCity = city === "lausanne"
    const cityValue = isLausanneCity ? "custom" : city
    setIsLausanne(isLausanneCity)
    toggleValue({ name: "city", value: cityValue }, true, ["custom_city"])
    onCityChange(cityValue)
    setShowCityForm(cityValue === "custom" && !isLausanneCity)
  }

  useEffect(() => {
    const preset = document.body.dataset?.city
    if (preset !== "custom") {
      setInitialName(props.cities.find((c) => c.value === preset).name)
      if (!inFormValues({ name: "city", value: `${preset}` }))
        toggleValue({ name: "city", value: `${preset}` }, true)
      onCityChange(preset)
    }
    setInitialValue(preset)
  }, [])

  return (
    <>
      <fieldset>
        <SelectOptions
          name="city"
          options={props.cities}
          required={true}
          initialValue={initialValue}
          initialName={initialName}
          setInitialName={setInitialName}
          setLogo={setLogo}
          onChange={handleCityChange}
        />
      </fieldset>
      {showCityForm
        ? <fieldset>
            <PlacesTextInput
              ep={ep}
              name={"custom_city"}
              setLogo={setLogo}
              title={gettext("Postal code or city")}
            />
          </fieldset>
        : null}
      {isLausanne
        ? <div>
            <div>
              <p>
                {gettext(
                  "Lausanne has its own reporting tool. Please submit your report using this tool.",
                )}
              </p>
            </div>
            <a
              class="external-link button"
              href="https://www.lausanne.ch/apps/webforms/harcelement/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {"https://www.lausanne.ch/apps/webforms/harcelement/"}
            </a>
          </div>
        : null}
    </>
  )
}

const SelectOptions = (props) => {
  const detailsRef = useRef("")
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const optionRefs = useRef([])

  const handleKeyDown = (e) => {
    const options = props.options
    if (options.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setFocusedIndex((prevIndex) => {
          const newIndex = prevIndex < options.length - 1 ? prevIndex + 1 : 0
          if (optionRefs.current[newIndex]) {
            optionRefs.current[newIndex].scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }
          return newIndex
        })
        break
      case "ArrowUp":
        e.preventDefault()
        setFocusedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : options.length - 1
          if (optionRefs.current[newIndex]) {
            optionRefs.current[newIndex].scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }
          return newIndex
        })
        break
      case "Enter":
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          const selectedOption = options[focusedIndex]
          props.onChange(selectedOption.value)
          props.setInitialName(selectedOption.name)
          detailsRef.current?.removeAttribute("open")
          setFocusedIndex(-1)
        }
        break
      case "Escape":
        e.preventDefault()
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
        break
      case "Tab":
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
        break
    }
  }

  const handleBlur = () => {
    // Small delay to allow click events to fire before closing
    setTimeout(() => {
      if (detailsRef.current?.hasAttribute("open")) {
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
      }
    }, 200)
  }

  return (
    <details
      class={`custom-select${props.required ? " required" : ""}`}
      ref={detailsRef}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <summary>
        {props.initialName}
        <Icon name="down" style="down" />
        <Icon name="close" style="close" />
      </summary>
      <div>
        {props.options.map((opt, index) => (
          <label
            key={`opt-${props.name}-${opt.value}`}
            ref={(el) => (optionRefs.current[index] = el)}
            class={focusedIndex === index ? "focused" : ""}
          >
            <span>{opt.name}</span>
            <input
              name={props.name}
              value={opt.value}
              type="radio"
              onChange={(v) => {
                const val = v.currentTarget.value
                props.onChange(val)
                props.setInitialName(opt.name)
                detailsRef.current?.removeAttribute("open")
                setFocusedIndex(-1)
              }}
              onFocus={() => setFocusedIndex(index)}
              required={props.required}
            />
          </label>
        ))}
      </div>
    </details>
  )
}

const PlacesTextInput = (props) => {
  const { name, title, setLogo } = props
  const { ep, toggleValue } = useFormContext()
  const [options, setOptions] = useState([])
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const optionRefs = useRef([])

  const detailsRef = useRef("")
  const inputRef = useRef("")

  const callOptions = debounce((v) => {
    ep.getData(`places=${v}`).then((epData) => {
      setOptions(epData.places || [])
    })
  }, 200)

  const handleKeyDown = (e) => {
    // Don't interfere with input typing
    if (e.target.tagName === "INPUT" && e.target.type === "text") {
      return
    }

    const currentOptions = options.length > 0 ? options : []
    if (currentOptions.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex < currentOptions.length - 1 ? prevIndex + 1 : 0
          if (optionRefs.current[newIndex]) {
            optionRefs.current[newIndex].scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }
          return newIndex
        })
        break
      case "ArrowUp":
        e.preventDefault()
        setFocusedIndex((prevIndex) => {
          const newIndex =
            prevIndex > 0 ? prevIndex - 1 : currentOptions.length - 1
          if (optionRefs.current[newIndex]) {
            optionRefs.current[newIndex].scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }
          return newIndex
        })
        break
      case "Enter":
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < currentOptions.length) {
          const selectedOption = currentOptions[focusedIndex]
          const ref = inputRef.current
          if (ref) ref.value = selectedOption.name
          setLogo(selectedOption.name)
          toggleValue(
            {
              name: name,
              value: selectedOption.value,
              label: selectedOption.name,
            },
            true,
          )
          detailsRef.current?.removeAttribute("open")
          setFocusedIndex(-1)
        }
        break
      case "Escape":
        e.preventDefault()
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
        break
      case "Tab":
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
        break
    }
  }

  const handleBlur = () => {
    // Small delay to allow click events to fire before closing
    setTimeout(() => {
      if (detailsRef.current?.hasAttribute("open")) {
        detailsRef.current?.removeAttribute("open")
        setFocusedIndex(-1)
      }
    }, 200)
  }

  return (
    <details
      class={`custom-select${props.required ? " required" : ""}`}
      ref={detailsRef}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onToggle={(_e) => {
        // Focus the input when the details element is opened
        if (detailsRef.current?.hasAttribute("open")) {
          inputRef.current?.focus()
        }
      }}
    >
      <summary>
        <input
          type="text"
          placeholder={title}
          ref={inputRef}
          onInput={(e) => {
            detailsRef.current?.setAttribute("open", "")
            const val = e.target?.value
            if (val.length > 2) callOptions(val)
            else {
              setOptions([])
            }
          }}
          onKeyDown={(e) => {
            // Allow normal typing in the input field
            if (
              e.key === "ArrowDown" ||
              e.key === "ArrowUp" ||
              e.key === "Enter"
            ) {
              // Move focus to the options list when navigation keys are pressed
              e.preventDefault()
              if (options.length > 0) {
                setFocusedIndex(e.key === "ArrowDown" ? 0 : options.length - 1)
              }
            }
          }}
          onFocus={(_e) => {
            // Open the dropdown when input is focused
            detailsRef.current?.setAttribute("open", "")
          }}
          required
        />
      </summary>
      <div>
        {options.length > 0
          ? options.map((opt, index) => (
              <label
                key={`opt-${props.name}-${opt.value}`}
                ref={(el) => (optionRefs.current[index] = el)}
                class={focusedIndex === index ? "focused" : ""}
              >
                <span>{opt.name}</span>
                <input
                  name={name}
                  value={opt.value}
                  type="radio"
                  onChange={(v) => {
                    const val = v.currentTarget.value
                    const ref = inputRef.current
                    if (ref) ref.value = opt.name
                    setLogo(opt.name)
                    toggleValue(
                      { name: name, value: val, label: opt.name },
                      true,
                    )
                    detailsRef.current?.removeAttribute("open")
                    setFocusedIndex(-1)
                  }}
                  onFocus={() => setFocusedIndex(index)}
                  required
                />
              </label>
            ))
          : <label key={`opt-${props.name}-none`}>
              <span>
                {inputRef.current && inputRef.current.value.length > 2
                  ? gettext("No city found")
                  : gettext("...")}
              </span>
              <input
                name={"custom_city"}
                type="radio"
                onClick={(e) => {
                  e.preventDefault()
                }}
                onChange={(e) => {
                  e.preventDefault()
                }}
                onKeyDown={(e) => {
                  e.preventDefault()
                }}
                required
              />
            </label>}
      </div>
    </details>
  )
}
